import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useAlerts } from "context/AlertContext";
import {
  IGetOrganizationsRequestParams,
  IOrganizationResponse,
} from "models/organizations.models";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import {
  MAX_API_RESULT_SIZE,
  QueryAPIKey,
} from "utils/constants/api.constants";
import handleRetryFailure from "utils/helpers/handleRetryFailure";
import { TErrorData } from "utils/types";

export default function useOrganizations(
  params: IGetOrganizationsRequestParams = { size: MAX_API_RESULT_SIZE },
  enabled?: boolean,
) {
  const safeParams = params || {};

  const { t } = useTranslation("common", {
    keyPrefix: "notifications",
  });

  const { showAlert } = useAlerts();

  const { data, status, isLoading, isError, isSuccess, error } = useQuery<
    AxiosResponse<IOrganizationResponse>,
    AxiosError<TErrorData>,
    IOrganizationResponse
  >({
    queryKey: [QueryAPIKey.GetOrganizations, params],
    queryFn: () =>
      services.organizations
        .getOrganizations(safeParams)
        .then((res) => res.data),
    retry: handleRetryFailure(() =>
      showAlert({
        severity: AlertSeverity.Error,
        message: t("getOrganizationsFailure"),
      }),
    ),
    enabled: enabled ?? true,
    staleTime: Infinity,
  });

  return {
    organizations: data,
    isLoading,
    isError,
    isSuccess,
    error,
    status,
  };
}
